import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    OnDestroy,
    Output,
    QueryList,
    ViewChildren
} from '@angular/core';
import { select } from '@angular-redux/store';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, BaseComponent, Language, LogService } from '@ridango/core';
import { HelperService } from '../../../services/helper.service';
import { FeatureFlagService } from '@ridango/feature-flags';
import Env from '@ridango/core/env';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ApplicationFacade, CustomerAccountsFacade, CustomerFacade } from '@ridango/components';
import { SelectLanguageDialogComponent } from '../select-language-dialog/select-language-dialog.component';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { PermissionsFacade, pushNotificationReceived } from '@ridango/components/lib/permissions';
import { CountInfo, MessagesService } from '../../../../services/messages.service';
import { Actions, ofType } from '@ngrx/effects';
@Component({
    template: ''
})
export abstract class NewMobileMenuAbstractComponent extends BaseComponent implements OnInit, OnDestroy {
    @select(['application', 'assetsPath']) assetsPath$;
    @select(['core', 'selectedLanguage']) selectedLanguage$;
    @select(['core', 'region', 'languages']) languages$;
    public profile$: Observable<any> = this.customerFacade.profile$;
    protected featureFlags: FeatureFlagService;
    @Input() mobileMenuOpen = false;
    @Input() loggedInCustomer: string;
    @Input() isContrast: boolean;
    @Output() didToggleMobileMenu = new EventEmitter<any>();
    @Output() didCloseMobileMenu = new EventEmitter<any>();
    @Output() didLanguageChange = new EventEmitter<Language>();
    @Output() didLogOut = new EventEmitter<any>();
    @Output() themeToggle: EventEmitter<void> = new EventEmitter();
    isContrast$: Observable<boolean> = this.applicationFacade.highContrast$;
    @ViewChildren('menuItem') menuItemEls: QueryList<ElementRef>;
    hasContrastChanged = false;
    receivedMessagesCount$: Observable<CountInfo>;

    private englishTranslations = this.translate.store.translations['en'];

    protected constructor(
        protected ls: LogService,
        public ridangoAuth: AuthService,
        public helperService: HelperService,
        public translate: TranslateService,
        @Inject('ENV') public env: Env,
        protected dialog: MatDialog,
        protected applicationFacade: ApplicationFacade,
        protected customerFacade: CustomerFacade,
        public permissionsFacade: PermissionsFacade,
        public actions$: Actions,
        public messagesService: MessagesService,
        public analyticsService: AnalyticsService,
        private customerAccountsFacade: CustomerAccountsFacade
    ) {
        super();
    }

    ngOnInit() {
        this.customerFacade.getCustomerProfile();
        this.customerFacade.profile$.pipe(takeUntil(this.componentDestroyed$)).subscribe((d) => {
            if (d?.deleted) {
                this.ridangoAuth.signOut();
                this.customerFacade.clearCustomerProfile();
            }
        });

        this.receivedMessagesCount$ = this.messagesService.getReceivedMessagesCount();
        this.actions$
            .pipe(
                takeUntil(this.componentDestroyed$),
                ofType(pushNotificationReceived),
                tap(() => {
                    this.receivedMessagesCount$ = this.messagesService.getReceivedMessagesCount();
                })
            )
            .subscribe();
    }

    toggleMobileMenu() {
        this.ls.log(this, 'toggleMobileMenu', '');
        this.didToggleMobileMenu.emit();
    }

    closeMobileMenu(linkClicked = false, link?: any) {
        link &&
            this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
                category: TrackedEventCategory.MENU,
                name: `${this.englishTranslations[link.title]}`
            });

        if (linkClicked && this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE) {
            return;
        }

        this.ls.log(this, 'closeMobileMenu', '');
        this.didCloseMobileMenu.emit();
    }

    get isVastmanlandApp(): boolean {
        return this.env.APP_NAME === 'vastmanland-app';
    }

    onPhoneNrClick() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.MENU,
            name: `${this.englishTranslations['FOOTER_PHONE_TEXT']}`
        });
    }

    redirectToMinaSidor(): void {
        if (this.env.HEADER?.REDIRECT_TO_MY_ACCOUNT_WEB) {
            const token = this.ridangoAuth.getLocalAccessToken();
            const redirectUrl = `${this.env.HEADER?.REDIRECT_TO_MY_ACCOUNT_WEB}/${this.translate.currentLang}/home`;

            window.location.href = `${redirectUrl}?tokenSSO=${token}`;
        }
    }

    logout() {
        this.ls.log(this, 'logOut', '');
        this.didLogOut.emit();
        this.customerAccountsFacade.clearSelectedAccount();
        this.permissionsFacade.logoutSuccess();
    }

    languageChanged(language: Language) {
        this.didLanguageChange.emit(language);
        this.didCloseMobileMenu.emit();
    }

    openSelectLanguageMenu() {
        this.dialog
            .open(SelectLanguageDialogComponent, {
                data: {
                    changeLang: this.languageChanged
                },
                maxWidth: '600px',
                width: '90%'
            })
            .afterClosed()
            .pipe(take(1), filter(Boolean))
            .subscribe((result: Language) => {
                if (this.env.SEND_CUSTOMER_LANGUAGE_TO_BACKEND) {
                    this.customerFacade.sendCustomerLang(result.code);
                }
                this.didLanguageChange.emit(result);
            });
    }

    toggleContrast(): void {
        this.hasContrastChanged = true;
        this.themeToggle.emit();
    }
}
